import React, { useState, useEffect, useRef } from "react";
import {
  XIcon,
  LightbulbIcon,
  SparklesIcon,
  CheckIcon,
  XCircleIcon,
  RotateCcwIcon,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { enhancePrompt } from "../../lib/sumpodAIService";
import toast from "react-hot-toast";

interface EnhancePromptDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentPrompt: string;
  currentMataPelajaran: string;
  currentJenjangPendidikan: string;
  currentTipePertanyaan: string;
  currentKelas?: string;
  onAccept: (enhancedPrompt: string) => void;
}

export const EnhancePromptDialog: React.FC<EnhancePromptDialogProps> = ({
  isOpen,
  onClose,
  currentPrompt,
  currentMataPelajaran,
  currentJenjangPendidikan,
  currentTipePertanyaan: _currentTipePertanyaan,
  currentKelas,
  onAccept,
}) => {
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhancedPrompt, setEnhancedPrompt] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Auto-start enhancement when dialog opens
  useEffect(() => {
    if (isOpen && currentPrompt.trim()) {
      // Reset state first
      setIsEnhancing(false);
      setEnhancedPrompt(null);

      // Start enhancement automatically after a brief delay
      const timer = setTimeout(() => {
        handleGenerateEnhancedPrompt();
      }, 300);

      return () => clearTimeout(timer);
    } else if (isOpen) {
      // Reset state if dialog opens without valid prompt
      setIsEnhancing(false);
      setEnhancedPrompt(null);
    }
  }, [isOpen, currentPrompt]);

  // Auto-scroll to bottom on mobile when enhanced prompt is generated
  useEffect(() => {
    if (enhancedPrompt && !isEnhancing && panelRef.current) {
      const isMobile = window.innerWidth < 1024; // lg breakpoint
      if (isMobile) {
        setTimeout(() => {
          if (panelRef.current) {
            panelRef.current.scrollTo({
              top: panelRef.current.scrollHeight,
              behavior: "smooth",
            });
          }
        }, 100);
      }
    }
  }, [enhancedPrompt, isEnhancing]);

  const handleGenerateEnhancedPrompt = async () => {
    if (
      !currentPrompt.trim() ||
      !currentMataPelajaran.trim() ||
      !currentJenjangPendidikan.trim()
    ) {
      toast.error("Mohon lengkapi semua field yang diperlukan");
      return;
    }

    setIsEnhancing(true);
    setEnhancedPrompt(null);

    try {
      const result = await enhancePrompt({
        currentPrompt: currentPrompt.trim(),
        mataPelajaran: currentMataPelajaran.trim(),
        jenjangPendidikan: currentJenjangPendidikan.trim(),
        type: "learning-objectives",
        ...(currentKelas ? { kelas: currentKelas } : {}),
      });

      if (result.success && result.enhancedPrompt) {
        setEnhancedPrompt(result.enhancedPrompt);

        if (result.fallback) {
          toast.success(
            "Prompt assessment berhasil ditingkatkan menggunakan template!",
            { duration: 3000 }
          );
        } else {
          toast.success("Prompt assessment berhasil ditingkatkan dengan AI!");
        }
      } else {
        throw new Error(result.error || "Failed to enhance assessment prompt");
      }
    } catch (error) {
      console.error("💥 Error enhancing assessment prompt:", error);

      // Provide fallback enhanced learning objectives in list format
      const fallbackPrompt = `1. Siswa mampu menjelaskan konsep utama mengenai ${currentPrompt}
2. Siswa dapat menganalisis relevansi ${currentPrompt} dalam konteks kehidupan sehari-hari
3. Siswa mampu mengidentifikasi istilah penting yang berkaitan dengan ${currentPrompt}
4. Siswa dapat menyusun rangkuman informatif tentang ${currentPrompt}
5. Siswa mampu menyajikan hasil kajian mengenai ${currentPrompt} secara kreatif
6. Siswa dapat mengevaluasi informasi yang berkaitan dengan ${currentPrompt}
7. Siswa mampu menerapkan pengetahuan tentang ${currentPrompt} dalam situasi nyata`;

      setEnhancedPrompt(fallbackPrompt);
      toast.error("Gagal menghubungi AI, menggunakan template peningkatan.", {
        duration: 4000,
      });
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleAccept = () => {
    if (enhancedPrompt) {
      onAccept(enhancedPrompt);
      onClose();
    }
  };

  const handleCancel = () => {
    setEnhancedPrompt(null);
    setIsEnhancing(false);
    onClose();
  };

  const handleRetry = () => {
    handleGenerateEnhancedPrompt();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-50">
      {/* Responsive slide-in panel */}
      <div
        ref={panelRef}
        className={`w-full sm:w-3/4 lg:w-1/2 h-full bg-white transform transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        } overflow-y-auto`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] rounded-lg flex items-center justify-center">
              <SparklesIcon className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg sm:text-xl font-semibold text-[#373e46]">
              Enhance Learning Objectives
            </h2>
          </div>
          <button
            onClick={handleCancel}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          >
            <XIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 flex-1">
          {/* Mata Pelajaran */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-[#373e46]">
              Mata Pelajaran
            </label>
            <Input
              value={currentMataPelajaran}
              readOnly
              className="h-10 sm:h-12 rounded-xl border-2 border-[#e3e6eb] bg-gray-50 text-[#373e46]"
            />
          </div>

          {/* What Was Changed - Only show when enhanced prompt is available */}
          {enhancedPrompt && !isEnhancing && (
            <div className="space-y-3">
              <h3 className="text-base sm:text-lg font-semibold text-[#373e46]">
                What Was Changed
              </h3>
              <div className="bg-blue-50 rounded-xl p-3 sm:p-4 space-y-3 border border-blue-200">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-xs sm:text-sm text-blue-700 leading-relaxed">
                    Enhanced the assessment criteria by specifying comprehensive
                    evaluation methods and clear indicators.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-xs sm:text-sm text-blue-700 leading-relaxed">
                    Added Bloom&apos;s Taxonomy levels (C1-C6) to ensure varied
                    difficulty and cognitive assessment.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-xs sm:text-sm text-blue-700 leading-relaxed">
                    Included rubric development and objective assessment
                    criteria for better evaluation standards.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Prompt Section */}
          <div className="space-y-3">
            <h3 className="text-base sm:text-lg font-semibold text-[#373e46]">
              Enhanced Learning Objectives
            </h3>

            {isEnhancing ? (
              /* Loading State */
              <div className="bg-gradient-to-br from-[#f8f9ff] to-[#f3f4ff] rounded-xl p-4 sm:p-6 border border-[#e3e6eb] flex flex-col items-center justify-center min-h-[150px] sm:min-h-[200px]">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] rounded-full flex items-center justify-center shadow-lg relative mb-4">
                  <SparklesIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white animate-pulse" />
                  <div className="absolute inset-0 w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] rounded-full animate-ping opacity-20"></div>
                </div>
                <p className="text-xs sm:text-sm text-[#6b2cfe] font-medium mb-2">
                  Meningkatkan prompt...
                </p>
                <p className="text-xs text-gray-500 text-center">
                  AI sedang menganalisis dan memperbaiki prompt Anda
                </p>
              </div>
            ) : enhancedPrompt ? (
              /* Enhanced Prompt Display */
              <div className="bg-gradient-to-br from-[#f8f9ff] to-[#f3f4ff] rounded-xl p-3 sm:p-4 border border-[#e3e6eb]">
                <div className="text-xs sm:text-sm text-[#6b2cfe] leading-relaxed whitespace-pre-wrap">
                  {enhancedPrompt}
                </div>
              </div>
            ) : (
              /* Error or Empty State */
              <div className="bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-200 flex flex-col items-center justify-center min-h-[150px] sm:min-h-[200px]">
                <LightbulbIcon className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mb-3" />
                <p className="text-xs sm:text-sm text-gray-500 text-center">
                  {currentPrompt.trim()
                    ? "Terjadi kesalahan saat meningkatkan prompt"
                    : "Tidak ada prompt yang dapat ditingkatkan"}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer - Sticky at bottom */}
        <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4 sm:p-6">
          {isEnhancing ? (
            /* Loading Button */
            <Button
              disabled
              className="w-full bg-gray-300 text-gray-500 px-6 sm:px-8 py-2 sm:py-3 rounded-xl font-semibold cursor-not-allowed flex items-center justify-center gap-2 h-10 sm:h-auto"
            >
              <SparklesIcon className="w-5 h-5 animate-pulse" />
              <span className="text-sm sm:text-base">
                Meningkatkan Prompt...
              </span>
            </Button>
          ) : enhancedPrompt ? (
            /* Accept/Cancel/Retry Buttons */
            <div className="space-y-2 sm:space-y-3">
              <div className="flex gap-2 sm:gap-3">
                <Button
                  onClick={handleCancel}
                  variant="outline"
                  className="flex-1 border-gray-300 text-[#373e46] hover:bg-gray-50 px-4 sm:px-8 py-2 sm:py-3 rounded-xl font-semibold flex items-center justify-center gap-2 h-10 sm:h-auto"
                >
                  <XCircleIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="text-sm sm:text-base">Batal</span>
                </Button>
                <Button
                  onClick={handleAccept}
                  className="flex-1 bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white px-4 sm:px-8 py-2 sm:py-3 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-2 h-10 sm:h-auto"
                >
                  <CheckIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="text-sm sm:text-base">Terima</span>
                </Button>
              </div>
              <Button
                onClick={handleRetry}
                variant="outline"
                className="w-full border-[#6b2cfe] text-[#6b2cfe] hover:bg-[#6b2cfe] hover:text-white px-4 sm:px-8 py-2 sm:py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 h-10 sm:h-auto"
              >
                <RotateCcwIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm sm:text-base">Coba Lagi</span>
              </Button>
            </div>
          ) : (
            /* Retry Button for error state */
            <Button
              onClick={handleRetry}
              disabled={!currentPrompt.trim()}
              className={`w-full px-4 sm:px-8 py-2 sm:py-3 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-2 h-10 sm:h-auto ${
                currentPrompt.trim()
                  ? "bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              <RotateCcwIcon className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-sm sm:text-base">Coba Lagi</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
